/**
 * ErpPeriodDropdown
 * Dropdown de opções no padrão do gráfico de projeção financeira.
 */

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react'
import './ErpPeriodDropdown.css'

function ErpPeriodDropdown({
  label = 'Período:',
  value,
  options = [],
  onChange,
  className = '',
  align = 'right',
  disabled = false,
  portal = false,
  ariaLabel,
  searchable = false
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [menuBox, setMenuBox] = useState(null)
  const containerRef = useRef(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const searchRef = useRef(null)

  const selectedLabel = options.find((opt) => opt.value === value)?.label
    ?? (value !== '' && value != null ? String(value) : 'Selecionar')

  useEffect(() => {
    if (!open) return undefined

    const handleClickOutside = (event) => {
      const target = event.target
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return
      setOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (open) return undefined
    setQuery('')
    return undefined
  }, [open])

  useEffect(() => {
    if (!open || !searchable) return undefined
    const timer = window.setTimeout(() => searchRef.current?.focus(), 0)
    return () => window.clearTimeout(timer)
  }, [open, searchable])

  useLayoutEffect(() => {
    if (!open || !portal) return undefined

    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const maxWidth = Math.max(160, window.innerWidth - 16)
      const width = Math.min(Math.max(rect.width, searchable ? 220 : 180), maxWidth)
      let left = align === 'left' ? rect.left : rect.right - width
      if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8
      if (left < 8) left = 8
      const spaceBelow = window.innerHeight - rect.bottom - 16
      const spaceAbove = rect.top - 16
      const openUp = spaceBelow < 220 && spaceAbove > spaceBelow
      const maxHeight = Math.max(160, Math.min(320, openUp ? spaceAbove : spaceBelow))
      setMenuBox({
        top: openUp ? 'auto' : rect.bottom + 6,
        bottom: openUp ? window.innerHeight - rect.top + 6 : 'auto',
        left,
        width,
        maxHeight
      })
    }

    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, portal, align, searchable])

  const needle = query.trim().toLowerCase()
  const visibleOptions = searchable && needle
    ? options.filter((opt) => `${opt.label} ${opt.value}`.toLowerCase().includes(needle))
    : options

  const menu = open ? (
    <ul
      className="balance-projection-chart-period-menu"
      role="listbox"
      style={menuBox?.maxHeight ? { maxHeight: menuBox.maxHeight } : undefined}
    >
      {searchable && (
        <li className="erp-period-dropdown-search">
          <input
            ref={searchRef}
            type="search"
            value={query}
            placeholder="Buscar"
            aria-label="Buscar"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.preventDefault()
              if (event.key === 'Escape') setOpen(false)
            }}
          />
        </li>
      )}
      {visibleOptions.length === 0 && (
        <li className="erp-period-dropdown-empty">Nenhum resultado</li>
      )}
      {visibleOptions.map((opt, index) => (
        <li key={String(opt.value)}>
          <button
            type="button"
            role="option"
            aria-selected={value === opt.value}
            className={`balance-projection-chart-period-option${value === opt.value ? ' is-selected' : ''}`}
            style={{ animationDelay: `${Math.min(index, 12) * 28}ms` }}
            onClick={() => {
              onChange?.(opt.value)
              setOpen(false)
            }}
          >
            {opt.label}
          </button>
        </li>
      ))}
    </ul>
  ) : null

  const portaledMenu = portal && menu && menuBox
    ? createPortal(
      <div
        ref={menuRef}
        className="erp-period-dropdown erp-period-dropdown-portal"
        data-align={align}
        style={{
          position: 'fixed',
          zIndex: 10052,
          top: menuBox.top,
          bottom: menuBox.bottom,
          left: menuBox.left,
          width: menuBox.width,
          maxHeight: menuBox.maxHeight
        }}
      >
        {menu}
      </div>,
      document.body
    )
    : null

  return (
    <div
      className={`balance-projection-chart-period erp-period-dropdown ${className}`.trim()}
      ref={containerRef}
      data-align={align}
    >
      {label && <span className="balance-projection-chart-period-label">{label}</span>}
      <div className="balance-projection-chart-period-dropdown">
        <button
          ref={triggerRef}
          type="button"
          className="balance-projection-chart-period-trigger"
          onClick={() => {
            if (disabled) return
            setOpen((prev) => !prev)
          }}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={ariaLabel || undefined}
          disabled={disabled}
        >
          <span className="balance-projection-chart-period-value">{selectedLabel}</span>
          <ChevronDown
            size={14}
            className={`balance-projection-chart-period-chevron${open ? ' is-open' : ''}`}
            aria-hidden
          />
        </button>
        {portal ? portaledMenu : menu}
      </div>
    </div>
  )
}

export default ErpPeriodDropdown
